import React, {ReactNode, useEffect, useRef} from "react";
import {useFlag, useFlagsStatus} from "@unleash/proxy-client-react";
import {UnavailablePage} from "./pages/error/UnavailablePage";
import ReactDOM from "react-dom/client";
import "./main.scss";
import Barnehagepris from "./pages/Barnehagepris";
import LoadingSpinner from "./components/LoadingSpinner";
import {createBrowserRouter, RouterProvider} from "react-router-dom";
import {submitError} from "./errorReport";
import Header from "./components/Header";
import {PktButton, PktFooterSimple, PktModal,} from "@oslokommune/punkt-react";
import Feedback from "./components/Feedback";

import AvsluttPrissoknad from "./pages/AvsluttPrissoknad/AvsluttPrissoknad";
import {postFeedback} from "./api/feedback";
import ErrorHandler from "./pages/error/ErrorHandler";
import {DinBarnehageside} from "./pages/DinBarnehageside/DinBarnehageside";
import {
  AVSLUTT_PRIS_SØKNAD_ROUTE,
  DINE_SAKER_ROUTE,
  DINE_SØKNADER_ROUTE, OPPSIGELSE_ROUTE, PERMISJON_ROUTE,
  PLASSERING_ROUTE,
  PRIS_SØKNAD_ROUTE,
  PRIS_SØKNADER_ROUTE
} from "./routes";
import Layout from "./components/Layout";
import {DineSaker} from "./pages/DinBarnehageside/DineSaker";
import {AuthContextProvider, useAuthContext} from '@oslokommune/auth-bff/react'
import Barnehageprissoknad from "./pages/Barnehageprissoknad/Barnehageprissoknad";
import {BackLinkInternNavigasjon} from "./components/BackLinkInternNavigasjon";
import {config} from "./environment";
import {FeatureFlagProvider} from "./components/FeatureFlagProvider";
import {Oppsigelse} from "./pages/Oppsigelse";
import {OppsigelseKvittering} from "./pages/Oppsigelse/OppsigelseKvittering";
import {OppsigelseStatus} from "./pages/OppsigelseStatus";
import {PermisjonKvittering} from "./pages/Permisjon/PermisjonKvittering";
import {Permisjonssøknad} from "./pages/Permisjon";
import {PermisjonStatus} from "./pages/PermisjonStatus";
import {ExpiredSessionModal} from "./components/ExpiredSessionModal";

window.addEventListener("error", (errorEvent) => {
  console.error("window error event", errorEvent);
  submitError("Unhandled error", errorEvent);
});

const DineSakerPage = (
  <Layout>
    <BackLinkInternNavigasjon url={config.linkBarnehageBaseUrl} text={'Din barnehageside'}/>
    <DineSaker/>
  </Layout>
)

const router = createBrowserRouter([
  {
    path: "/",
    element:
      <Layout>
        <DinBarnehageside/>
      </Layout>
    ,
    errorElement: <ErrorHandler/>
  },
  {
    path: DINE_SØKNADER_ROUTE,
    element: DineSakerPage,
    errorElement:
      <ErrorHandler/>
  },
  {
    path: DINE_SAKER_ROUTE,
    element: DineSakerPage,
    errorElement:
      <ErrorHandler/>
  },
  {
    path: PRIS_SØKNADER_ROUTE,
    element: (
      <Layout>
        <BackLinkInternNavigasjon url={config.linkBarnehageBaseUrl} text={'Din barnehageside'}/>
        <Barnehagepris/>
      </Layout>
    ),
  },
  {
    path: `${PRIS_SØKNAD_ROUTE}/:id`,
    element: (
      <Layout>
        <BackLinkInternNavigasjon url={"/dine-saker"} text={'Dine saker'}/>
        <Barnehageprissoknad/>
      </Layout>
    ),
  },
  {
    path: AVSLUTT_PRIS_SØKNAD_ROUTE,
    element: (
      <Layout>
        <BackLinkInternNavigasjon url={"/barnehagepris"} text={"Dine søknader om barnehagepris"}/>
        <AvsluttPrissoknad/>
      </Layout>
    ),
  },
  {
    path: `${OPPSIGELSE_ROUTE}`,
    element: (
      <Layout>
        <BackLinkInternNavigasjon url={'/'} text={'Din barnehageside'}/>
        <Oppsigelse/>
      </Layout>
    )
  },
  {
    path: `${OPPSIGELSE_ROUTE}/kvittering`,
    element: (
      <Layout>
        <OppsigelseKvittering/>
      </Layout>
    )
  },
  {
    path: `${OPPSIGELSE_ROUTE}/:id`,
    element: (
      <Layout>
        <BackLinkInternNavigasjon url={"/dine-saker"} text={'Dine saker'}/>
        <OppsigelseStatus/>
      </Layout>
    )
  },
  {
    path: `${PERMISJON_ROUTE}`,
    element: (
      <Layout>
        <BackLinkInternNavigasjon url={'/'} text={'Din barnehageside'}/>
        <Permisjonssøknad/>
      </Layout>
    )
  },
  {
    path: `${PERMISJON_ROUTE}/kvittering`,
    element: (
      <Layout>
        <PermisjonKvittering/>
      </Layout>
    )
  },
  {
    path: `${PERMISJON_ROUTE}/:id`,
    element: (
      <Layout>
        <BackLinkInternNavigasjon url={"/dine-saker"} text={'Dine saker'}/>
        <PermisjonStatus/>
      </Layout>
    )
  },
]);

const Footer = () => {
  const isOsloKommuneNo = window.location.host?.endsWith("oslo.kommune.no")
  return (
    <PktFooterSimple
      className={"mt-size-32"}
      personvernOgInfoLink={"https://www.oslo.kommune.no/barnehage/personvernerklaring-for-barnehage/"}
      links={[
        {
          text: 'Kontakt oss',
          href: "https://www.oslo.kommune.no/barnehage/kontakt-barnehageadministrasjonen/",
          openInNewTab: true,
        },
      ]}
      openLinksInNewTab={true}
      includeConsent={isOsloKommuneNo}
      googleAnalyticsId={'GTM-MDQV35WS'}
    />
  )
};

const VedlikeholdsmodusGate = ({children}: { children: ReactNode }) => {
  const isVedlikeholdsmodus = useFlag('soknad-frontend.vedlikeholdsmodus')
  const {flagsReady, flagsError} = useFlagsStatus()

  if (!flagsReady && !flagsError) {
    return <LoadingSpinner/>
  }
  if (isVedlikeholdsmodus) {
    return <UnavailablePage/>
  }

  return <>{children}</>
}

const root = ReactDOM.createRoot(document.getElementById("root") as Element);
const fiveMinutes = 5 * 60 * 1000

root.render(
  <React.StrictMode>
    <FeatureFlagProvider>
      <VedlikeholdsmodusGate>
        <AuthContextProvider authRequired={true} loaderComponent={LoadingSpinner()} pollInterval={fiveMinutes}>
          <div style={{minHeight: "100vh", display: "grid", gridTemplateRows: "auto 1fr auto"}}>
            <Header/>
            <div>
              <RouterProvider router={router}/>
              <Feedback
                maxTextFeedbackLength={2000}
                onThumbsFeedback={(thumb) => {
                  postFeedback({
                    system: "kuen",
                    location: window.location.href,
                    ref: "index",
                    rating: thumb,
                  });
                }}
                onTextFeedback={async (text) => {
                  if (text) {
                    await postFeedback({
                      system: "kuen",
                      location: window.location.href,
                      ref: "index",
                      comment: text,
                    });
                  }
                }}
              />
            </div>
            <Footer/>
            <ExpiredSessionModal/>
          </div>
        </AuthContextProvider>
      </VedlikeholdsmodusGate>
    </FeatureFlagProvider>
  </React.StrictMode>
);
